<template>
    <component
        :is="disabled ? 'div' : 'form'"
        class="agent-definitions-form"
        :action="disabled ? undefined : formAction"
        method="POST"
        accept-charset="UTF-8"
        @submit="onSubmit"
    >
        <template v-if="!disabled">
            <input type="hidden" name="_token" :value="csrfToken">
            <input v-if="formMethod === 'PUT'" type="hidden" name="_method" value="PUT">
        </template>

        <div class="panel panel-default">
            <div class="panel-heading"><strong>{{ labels.definition }}</strong></div>
            <div class="panel-body">
                <div class="row">
                    <div class="col-md-4">
                        <tenant-account-select
                            v-model="definition.account_id"
                            :can-switch-account="canSwitchAccount"
                            :accounts-url="options.accountsUrl"
                            :label="labels.account + '*'"
                            :disabled="disabled || initialState.isEdit"
                            :error-message="fieldError('account_id')"
                        ></tenant-account-select>
                    </div>
                    <div class="col-md-4 form-group" :class="{ 'has-error': fieldError('slug') }">
                        <label class="control-label">{{ labels.slug }}*</label>
                        <input
                            v-model="definition.slug"
                            class="form-control"
                            type="text"
                            name="slug"
                            :disabled="disabled || initialState.isEdit"
                            :readonly="disabled || initialState.isEdit"
                        >
                        <p v-if="fieldError('slug')" class="help-block">{{ fieldError('slug') }}</p>
                    </div>
                    <div class="col-md-4 form-group" :class="{ 'has-error': fieldError('label') }">
                        <label class="control-label">{{ labels.label }}*</label>
                        <input
                            v-model="definition.label"
                            class="form-control"
                            type="text"
                            name="label"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                        <p v-if="fieldError('label')" class="help-block">{{ fieldError('label') }}</p>
                    </div>
                </div>
                <div class="row">
                    <div v-if="!initialState.isEdit" class="col-md-4 form-group" :class="{ 'has-error': fieldError('nhi_id') }">
                        <label class="control-label">{{ labels.nhiExisting }}</label>
                        <select
                            v-model="definition.nhi_id"
                            class="form-control"
                            name="nhi_id"
                            :disabled="disabled"
                        >
                            <option :value="null">{{ labels.nhiAutoProvision }}</option>
                            <option v-for="nhi in availableNhis" :key="nhi.id" :value="nhi.id">{{ nhi.label }}</option>
                        </select>
                        <p v-if="fieldError('nhi_id')" class="help-block">{{ fieldError('nhi_id') }}</p>
                    </div>
                    <div v-if="!initialState.isEdit && !linkingExistingNhi" class="col-md-4">
                        <tenant-user-select
                            v-model="definition.owner_user_id"
                            :account-id="definition.account_id"
                            :users-url="options.usersUrl"
                            :prefetched-users="tenantUsers"
                            name="owner_user_id"
                            :label="labels.nhiOwner"
                            :empty-label="labels.nhiOwnerNone"
                            :allow-empty="true"
                            :disabled="disabled"
                            :error-message="fieldError('owner_user_id')"
                            input-id="definition-owner-user-select"
                        ></tenant-user-select>
                    </div>
                    <div v-if="!initialState.isEdit" class="col-md-4 form-group" :class="{ 'has-error': fieldError('nhi_role_id') }">
                        <label class="control-label">{{ labels.nhiRole }}</label>
                        <select
                            v-model="definition.nhi_role_id"
                            class="form-control"
                            name="nhi_role_id"
                            :disabled="disabled"
                        >
                            <option :value="null">{{ labels.nhiRoleNone }}</option>
                            <option v-for="(label, value) in options.nhiRoles" :key="value" :value="Number(value)">{{ label }}</option>
                        </select>
                        <p v-if="fieldError('nhi_role_id')" class="help-block">{{ fieldError('nhi_role_id') }}</p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('runner') }">
                        <label class="control-label">{{ labels.runner }}*</label>
                        <select v-model="definition.runner" class="form-control" name="runner" :disabled="disabled">
                            <option v-for="(label, value) in options.runners" :key="value" :value="value">{{ label }}</option>
                        </select>
                        <p v-if="fieldError('runner')" class="help-block">{{ fieldError('runner') }}</p>
                    </div>
                    <div v-if="runnerHelp" class="col-md-9">
                        <div class="alert alert-info" style="margin-bottom: 0; padding: 8px 12px;">
                            {{ runnerHelp }}
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('permission') }">
                        <label class="control-label">{{ labels.permission }}*</label>
                        <select v-model="definition.permission" class="form-control" name="permission" :disabled="disabled">
                            <option v-for="(label, value) in options.permissions" :key="value" :value="value">{{ label }}</option>
                        </select>
                    </div>
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('status') }">
                        <label class="control-label">{{ labels.status }}*</label>
                        <select v-model="definition.status" class="form-control" name="status" :disabled="disabled">
                            <option v-for="(label, value) in options.statuses" :key="value" :value="value">{{ label }}</option>
                        </select>
                    </div>
                    <div v-if="!isGenericRunner" class="col-md-3 form-group" :class="{ 'has-error': fieldError('max_steps') }">
                        <label class="control-label">{{ labels.maxSteps }}</label>
                        <input
                            v-model.number="definition.max_steps"
                            class="form-control"
                            type="number"
                            name="max_steps"
                            :disabled="disabled"
                            :readonly="disabled"
                        >
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('provider') }">
                        <label class="control-label">{{ labels.provider }}</label>
                        <select
                            v-model="definition.provider"
                            class="form-control"
                            name="provider"
                            :disabled="disabled"
                            @change="loadModels"
                        >
                            <option value=""></option>
                            <option v-for="(label, value) in options.providers" :key="value" :value="value">{{ label }}</option>
                        </select>
                    </div>
                    <div class="col-md-3 form-group" :class="{ 'has-error': fieldError('model') }">
                        <label class="control-label">{{ labels.model }}</label>
                        <select v-model="definition.model" class="form-control" name="model" :disabled="disabled">
                            <option value=""></option>
                            <option v-for="model in models" :key="model.id" :value="model.id">{{ model.label }}</option>
                        </select>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-12 form-group" :class="{ 'has-error': fieldError('instructions') }">
                        <label class="control-label">{{ labels.instructions }}</label>
                        <textarea
                            v-model="definition.instructions"
                            class="form-control"
                            rows="5"
                            name="instructions"
                            :disabled="disabled"
                            :readonly="disabled"
                        ></textarea>
                    </div>
                </div>
                <div v-if="!isGenericRunner" class="row">
                    <div class="col-md-12 form-group" :class="{ 'has-error': fieldError('tool_keys') }">
                        <label class="control-label">{{ labels.toolKeys }}</label>
                        <div class="checkbox-list">
                            <label
                                v-for="(label, value) in options.toolKeys"
                                :key="value"
                                class="checkbox-inline"
                            >
                                <input
                                    v-model="definition.tool_keys"
                                    type="checkbox"
                                    name="tool_keys[]"
                                    :value="value"
                                    :disabled="disabled"
                                >
                                {{ label }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <agent-resource-editor
            v-if="!isGenericRunner"
            v-model="resources"
            :resource-types="options.resourceTypes"
            :labels="labels"
            :validation-errors="validationErrors"
            :disabled="disabled"
        ></agent-resource-editor>

        <nhi-panel
            v-if="nhi"
            v-model="nhi"
            :account-id="definition.account_id"
            :users-url="options.usersUrl"
            :prefetched-users="tenantUsers"
            :status-options="options.statuses"
            :nhi-role-options="options.nhiRoles"
            :labels="labels"
            :validation-errors="validationErrors"
            :disabled="disabled"
        ></nhi-panel>

        <div v-if="!hideActions" class="clearfix form-actions">
            <div class="pull-left">
                <button v-if="!disabled" type="submit" class="btn btn-success">{{ labels.save }}</button>
            </div>
            <div class="pull-right">
                <a :href="backUrl" class="btn btn-primary">{{ labels.back }}</a>
            </div>
        </div>
    </component>
</template>

<script>
import TenantAccountSelect from './TenantAccountSelect.vue';
import TenantUserSelect from './TenantUserSelect.vue';
import AgentResourceEditor from './AgentResourceEditor.vue';
import NhiPanel from './NhiPanel.vue';

export default {
    components: {
        TenantAccountSelect,
        TenantUserSelect,
        AgentResourceEditor,
        NhiPanel,
    },
    props: {
        formAction: {
            type: String,
            default: '',
        },
        formMethod: {
            type: String,
            default: 'POST',
        },
        csrfToken: {
            type: String,
            default: '',
        },
        backUrl: {
            type: String,
            default: '',
        },
        editUrl: {
            type: String,
            default: '',
        },
        initialState: {
            type: Object,
            required: true,
        },
        options: {
            type: Object,
            required: true,
        },
        validationErrors: {
            type: Object,
            default: () => ({}),
        },
        canSwitchAccount: {
            type: Boolean,
            default: false,
        },
        defaultAccountId: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideActions: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            definition: { ...this.initialState.definition },
            resources: this.prepareResources(this.initialState.resources || []),
            nhi: this.initialState.nhi ? { ...this.initialState.nhi } : null,
            models: [],
            tenantUsers: [...(this.options.tenantUsers || [])],
            availableNhis: [],
        };
    },
    computed: {
        labels() {
            return this.options.labels || {};
        },
        linkingExistingNhi() {
            return ! this.initialState.isEdit && this.definition.nhi_id !== null && this.definition.nhi_id !== '';
        },
        isGenericRunner() {
            return this.definition.runner === 'generic';
        },
        runnerHelp() {
            const help = this.options.runnerHelp || {};

            return help[this.definition.runner] || '';
        },
    },
    watch: {
        'definition.account_id': {
            async handler(accountId) {
                if (! this.initialState.isEdit && accountId > 0) {
                    await Promise.all([
                        this.refreshTenantUsers(accountId),
                        this.refreshAvailableNhis(accountId),
                    ]);
                }
            },
        },
        'definition.nhi_id'(value) {
            if (value !== null && value !== '') {
                this.definition.owner_user_id = null;
            }
        },
    },
    async mounted() {
        if (this.definition.provider) {
            await this.loadModels();
        }

        if (! this.initialState.isEdit && this.definition.account_id > 0) {
            await this.refreshAvailableNhis(this.definition.account_id);
        }
    },
    methods: {
        prepareResources(rows) {
            return rows.map((row, index) => ({
                ...row,
                _key: row.id ? `resource-${row.id}` : `resource-new-${index}`,
            }));
        },
        async loadModels() {
            if (! this.definition.provider || ! this.options.modelsUrl) {
                this.models = [];

                return;
            }

            const url = `${this.options.modelsUrl}?provider=${encodeURIComponent(this.definition.provider)}`;
            const response = await fetch(url, { headers: { Accept: 'application/json' } });

            if (! response.ok) {
                this.models = [];

                return;
            }

            const payload = await response.json();
            this.models = payload.models || [];

            if (payload.default_model && ! this.definition.model) {
                this.definition.model = payload.default_model;
            }
        },
        fieldError(field) {
            const messages = this.validationErrors[field];

            return Array.isArray(messages) ? messages[0] : '';
        },
        async refreshTenantUsers(accountId) {
            if (! this.options.usersUrl || accountId <= 0) {
                return;
            }

            const url = `${this.options.usersUrl}?account_id=${encodeURIComponent(accountId)}`;
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (! response.ok) {
                return;
            }

            const payload = await response.json();
            this.tenantUsers = payload.users || [];
        },
        async refreshAvailableNhis(accountId) {
            if (! this.options.nhiOptionsUrl || accountId <= 0) {
                this.availableNhis = [];

                return;
            }

            const url = `${this.options.nhiOptionsUrl}?account_id=${encodeURIComponent(accountId)}`;
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (! response.ok) {
                this.availableNhis = [];

                return;
            }

            const payload = await response.json();
            this.availableNhis = payload.nhis || [];

            if (
                this.definition.nhi_id !== null
                && ! this.availableNhis.some((nhi) => Number(nhi.id) === Number(this.definition.nhi_id))
            ) {
                this.definition.nhi_id = null;
            }
        },
        onSubmit(event) {
            if (this.disabled) {
                event.preventDefault();
            }
        },
    },
};
</script>
